import React, { useEffect, useState } from 'react';
import './LoanLedger.css';
import { maskAadhaar, maskPAN } from '../../utils/dataMaskingUtils';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';

export default function LoanLedger() {
  const [noDuesAccounts, setNoDuesAccounts] = useState([]);
  const [duesAccounts, setDuesAccounts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [error, setError] = useState('');

  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  // Function to fetch data from the backend
  useEffect(() => {
    async function fetchLoanData() {
      try {
        const response = await fetch(`${baseUrl}/api/getLoanLedgerData`);
        const data = await response.json();
        console.log('Fetched loan data:', data);
  
        // Use the backend's categorization directly (accounts with < 5 rupees are in noDuesAccounts)
        setNoDuesAccounts(data.noDuesAccounts || []);
        setDuesAccounts(data.duesAccounts || []);
      } catch (error) {
        console.error('Error fetching loan data:', error);
      }
    }
  
    fetchLoanData();
  }, []);

  // Function to go back to the previous page
  const handleBackClick = () => {
    window.history.back();
  };

  async function fetchAccountDetails(accountNo) {
    try {
      setError('');
      setLoading(true);
      setAccountDetails(null);
      const res = await fetch(`${baseUrl}/api/getAccountDetails/${encodeURIComponent(accountNo)}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to fetch account details');
      }
      const details = await res.json();
      if (!details || !details.loan) {
        throw new Error('Invalid data received');
      }
      setAccountDetails(details);
    } catch (e) {
      console.error(e);
      setError('Unable to load account details. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const openDialog = (accountNo) => {
    setSelectedAccount(accountNo);
    setDialogOpen(true);
    fetchAccountDetails(accountNo);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setAccountDetails(null);
    setError('');
    setLoading(false);
  };

  // Convert number to words function
  function convertToWords(number) {
    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const thousands = ["", "Thousand", "Million", "Billion"];

    if (number === 0) return "Zero";

    let word = "";
    let thousandIndex = 0;

    while (number > 0) {
        if (number % 1000 !== 0) {
            word = convertHundred(number % 1000) + thousands[thousandIndex] + " " + word;
        }
        number = Math.floor(number / 1000);
        thousandIndex++;
    }
    return word.trim();

    function convertHundred(num) {
        let result = "";

        if (num > 99) {
            result += units[Math.floor(num / 100)] + " Hundred ";
            num %= 100;
        }

        if (num > 10 && num < 20) {
            result += teens[num - 10] + " ";
        } else {
            result += tens[Math.floor(num / 10)] + " ";
            result += units[num % 10] + " ";
        }

        return result;
    }
  }

  // Download Form No 6
  const handleDownloadFormNo6 = () => {
    if (!accountDetails) return;

    const loan = accountDetails.loan;
    const debtor = accountDetails.debtor;
    const guarantor = accountDetails.guarantor;
    const formNo = loan.form_no || '';
    const loanDate = loan.date || '';
    const loanAmount = parseFloat(loan.loan_amount || 0);
    const period = parseInt(loan.period || 0);
    
    // Calculate maturity date
    const maturityDate = loanDate ? (() => {
      const date = new Date(loanDate);
      date.setDate(date.getDate() + period);
      return formatDateToDDMMYYYY(date);
    })() : '';

    const billContent = `
<style>
  body { font-family: Arial, sans-serif; padding: 20px; margin: 0; font-size: 12px; }
  .page { display: flex; justify-content: space-between; flex-direction: row; }
  .bill { width: 48%; border: 1px solid #000; padding: 20px; box-sizing: border-box; position: relative; }
  .bill-header { font-size: 1.5em; font-weight: bold; text-align: center; margin-top: 20px; }
  .bill-sub-header { text-align: center; margin-top: 5px; font-size: 1em; }
  .content { margin-top: 20px; line-height: 1; }
  .table { width: 100%; margin-top: 20px; border-collapse: collapse; }
  .table th, .table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 12px; }
  .table th { background-color: #f2f2f2; }
  .footer { display: flex; justify-content: space-between; margin-top: 50px; }
  .footer div { text-align: center; width: 30%; font-weight: bold; }
  .box-container { display: flex; justify-content: flex-start; margin-bottom: 20px; }
  .box { border: 1px solid #000; padding: 5px 10px; margin-right: 10px; text-align: left; font-size: 12px; background-color: #fff; }
  .box span { display: block; font-size: 14px; }
  .box small { display: block; font-size: 10px; }
  .gl-box { border: 1px solid #000; padding: 10px; font-size: 20px; font-weight: bold; background-color: #fff; margin-right: 10px; }
  .divider { border-top: 1px solid #000; margin-top: 10px; margin-bottom: 10px; }
</style>

<div class="page">
  <!-- First Bill -->
  <div class="bill">
    <div class="bill-header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="bill-sub-header">Kasgi</div>
    <div class="divider"></div>
    <div class="box-container">
      <div class="gl-box">GL</div>
      <div class="box">
        <span>No - <strong>${formNo}</strong></span>
        <span>Date - ${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</span>
      </div>
      <div class="box">
        <span><strong>FORM NO - 6</strong></span>
        <small>(See Rule 15)</small>
      </div>
    </div>
    <div class="divider"></div>
    <div class="content">
      <div><strong>Statement Showing the Details of the Loan</strong></div>
    </div>
    <table class="table">
      <tr><th>Name of Loanee</th><td>${debtor.name || ''}</td></tr>
      <tr><th>Address</th><td>${debtor.address || ''}</td></tr>
      <tr><th>Amount of Loan</th><td>₹ ${loanAmount.toFixed(2)}</td></tr>
      <tr><th>Date of Loan</th><td>${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</td></tr>
      <tr><th>Loan Maturity (if any)</th><td>${maturityDate}</td></tr>
      <tr><th>Rate of Interest (per annum)</th><td>16%</td></tr>
      <tr><th>Name of Guarantor</th><td>${guarantor.name || ''}</td></tr>
      <tr><th>Guarantor Address</th><td>${guarantor.address || ''}</td></tr>
      <tr><th>Document Details</th><td>General Loan</td></tr>
      <tr><th>Special Conditions (if any)</th><td>Cheque No.</td></tr>
      <tr><th>Name & Address of Money Lender</th><td>SHREE SIDDHARAMESHWAR FINANCE AND LEASING, Kasgi</td></tr>
    </table>
    <div class="footer">
      <div>Signature of Loanee</div>
      <div>Signature of Guarantor</div>
      <div>Signature of Money Lender</div>
    </div>
  </div>

  <!-- Second Bill (Duplicate Content) -->
  <div class="bill">
    <div class="bill-header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="bill-sub-header">Kasgi</div>
    <div class="divider"></div>
    <div class="box-container">
      <div class="gl-box">GL</div>
      <div class="box">
        <span>No - <strong>${formNo}</strong></span>
        <span>Date - ${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</span>
      </div>
      <div class="box">
        <span><strong>FORM NO - 6</strong></span>
        <small>(See Rule 15)</small>
      </div>
    </div>
    <div class="divider"></div>
    <div class="content">
      <div><strong>Statement Showing the Details of the Loan</strong></div>
    </div>
    <table class="table">
      <tr><th>Name of Loanee</th><td>${debtor.name || ''}</td></tr>
      <tr><th>Address</th><td>${debtor.address || ''}</td></tr>
      <tr><th>Amount of Loan</th><td>₹ ${loanAmount.toFixed(2)}</td></tr>
      <tr><th>Date of Loan</th><td>${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</td></tr>
      <tr><th>Loan Maturity (if any)</th><td>${maturityDate}</td></tr>
      <tr><th>Rate of Interest (per annum)</th><td>16%</td></tr>
      <tr><th>Name of Guarantor</th><td>${guarantor.name || ''}</td></tr>
      <tr><th>Guarantor Address</th><td>${guarantor.address || ''}</td></tr>
      <tr><th>Document Details</th><td>General Loan</td></tr>
      <tr><th>Special Conditions (if any)</th><td>Cheque No.</td></tr>
      <tr><th>Name & Address of Money Lender</th><td>SHREE SIDDHARAMESHWAR FINANCE AND LEASING, Kasgi</td></tr>
    </table>
    <div class="footer">
      <div>Signature of Loanee</div>
      <div>Signature of Guarantor</div>
      <div>Signature of Money Lender</div>
    </div>
  </div>
</div>
`;

    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Form No 6</title></head><body>');
    printWindow.document.write(billContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Download Interest Slip
  const handleDownloadInterestSlip = () => {
    if (!accountDetails) return;

    const loan = accountDetails.loan;
    const debtor = accountDetails.debtor;
    const loanDate = loan.date || '';
    const loanAmount = parseFloat(loan.loan_amount || 0);
    const period = parseInt(loan.period || 0);
    const accountNo = loan.account_no || '';
    
    // Calculate interest
    const interest = ((loanAmount * period * 16) / (365 * 100)).toFixed(2);
    const documentCharges = 10;
    const total = (parseFloat(interest) + documentCharges).toFixed(2);

    const slipContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${accountNo}</div>
      <div class="line"><strong>Date:</strong> ${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹ ${loanAmount.toFixed(2)}</div>
      <div class="line"><strong>Received from Sri:</strong> ${debtor.name || ''}</div>
    </div>
    <table class="table">
      <tr>
        <td>GLInterest A/c</td>
        <td class="right">${interest}</td>
      </tr>
      <tr>
        <td>Document Charges A/c</td>
        <td class="right">${documentCharges}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹ ${total}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${accountNo}</div>
      <div class="line"><strong>Date:</strong> ${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹ ${loanAmount.toFixed(2)}</div>
      <div class="line"><strong>Received from Sri:</strong> ${debtor.name || ''}</div>
    </div>
    <table class="table">
      <tr>
        <td>GLInterest A/c</td>
        <td class="right">${interest}</td>
      </tr>
      <tr>
        <td>Document Charges A/c</td>
        <td class="right">${documentCharges}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹ ${total}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;

    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
    printWindow.document.write(slipContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Download Debit Voucher
  const handleDownloadDebitVoucher = () => {
    if (!accountDetails) return;

    const loan = accountDetails.loan;
    const debtor = accountDetails.debtor;
    const loanDate = loan.date || '';
    const loanAmount = parseFloat(loan.loan_amount || 0).toFixed(2);
    const accountNo = loan.account_no || '';

    const slipContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${accountNo}</div>
      <div class="line"><strong>Date:</strong> ${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹ ${loanAmount}</div>
      <div class="line"><strong>Given to :</strong> ${debtor.name || ''}</div>
    </div>
    <table class="table">
      <tr>
        <td>Loan Amount A/c</td>
        <td class="right">₹ ${loanAmount}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹ ${loanAmount}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${accountNo}</div>
      <div class="line"><strong>Date:</strong> ${loanDate ? formatDateToDDMMYYYY(loanDate) : '__________'}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹ ${loanAmount}</div>
      <div class="line"><strong>Given to:</strong> ${debtor.name || ''}</div>
    </div>
    <table class="table">
      <tr>
        <td>Loan Amount A/c</td>
        <td class="right">₹ ${loanAmount}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹ ${loanAmount}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;

    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Debit Voucher</title></head><body>');
    printWindow.document.write(slipContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Download Application Form
  const handleDownloadApplicationForm = () => {
    if (!accountDetails) return;

    const loan = accountDetails.loan;
    const debtor = accountDetails.debtor;
    const guarantor = accountDetails.guarantor;
    const loanDate = loan.date || '';
    const loanAmount = parseFloat(loan.loan_amount || 0);
    const period = parseInt(loan.period || 0);
    const accountNo = loan.account_no || '';
    const monthlyInstallment = parseFloat(loan.monthly_installment || 0);
    
    // Calculate due date
    const dueDate = loanDate ? (() => {
      const date = new Date(loanDate);
      date.setDate(date.getDate() + period);
      return formatDateToDDMMYYYY(date);
    })() : '';

    const printContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  h2 { text-align: center; margin: 0; padding-bottom: 10px; }
  p { margin: 0; }
  .form-container { border: 2px solid #000; padding: 20px; margin: auto; width: 95%; font-size: 12px; }
  .header-section { text-align: center; margin-bottom: 15px; }
  .letter-section { margin-bottom: 15px; line-height: 1.5; }
  .loan-details { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
  .loan-details div { width: 45%; }
  .loan-details p { margin: 0; font-weight: bold; }
  table { width: 100%; margin-top: 20px; border-collapse: collapse; }
  table, th, td { border: 1px solid #ddd; padding: 8px; }
  th { background-color: #f2f2f2; font-weight: bold; text-align: left; }
  td { font-size: 14px; vertical-align: top; }
  .signature-section { display: flex; justify-content: space-between; margin-top: 50px; }
  .signature { text-align: center; font-weight: bold; width: 30%; }
  .photo-section { display: flex; justify-content: space-between; margin-top: 20px; }
  .photo-container { text-align: center; width: 45%; }
  .photo-container img { width: 100px; height: 100px; border-radius: 8px; }
</style>

<div class="form-container">
  <div class="header-section">
    <h2>SHREE SIDDHARAMESHWAR FINANCE AND LEASING</h2>
    <p>Kasgi</p>
  </div>

  <div class="letter-section">
    <p>To,</p>
    <p><strong>The Managing Partner</strong></p>
    <p>SHREE SIDDHARAMESHWAR FINANCE AND LEASING</p>
    <p>Kasgi</p>
    <br>
    <p>Sir,</p>
    <p>
      I/We request you to discunt for me/us the bill drawn by me/us for 
      <strong>${period || '___'}</strong> days at 
      <strong>16%</strong> percent of discount.
    </p>
    <p>
      I/We agree to abide by the rules of Time Bill Discount / Cheque Discount 
      as printed below, further agree to execute documents and collateral documents 
      required by you in your favour.
    </p>
    <br>
  </div>

  <div class="loan-details">
    <div>
      <p>Date: ${loanDate ? formatDateToDDMMYYYY(loanDate) : ''}</p>
    </div>
    <div>
      <p>Account no: ${accountNo}</p>
    </div>
    <div style="text-align: right;">
      <p><strong>Rs. ${loanAmount.toFixed(2)}</strong></p>
    </div>
  </div>

  <table>
    <tr>
      <th colspan="2">Debtor Details</th>
    </tr>
    <tr>
      <td>
        Aadhaar No : <strong>${debtor.aadhaar || ''}</strong><br>
        Loanee Name : <strong>${debtor.name || ''}</strong><br>
        Father's Name : <strong>${loan.father_name_debtor || ''}</strong><br>
        Address : <strong>${debtor.address || ''}</strong><br>
        Phone No : <strong>${debtor.phone || ''}</strong>
      </td>
      <td class="photo-container">
        ${debtor.photo ? `<img src="${debtor.photo}" alt="Debtor Photo">` : '<p>No photo</p>'}
      </td>
    </tr>
  </table>
  <table>
    <tr>
      <th colspan="2">Guarantor Details</th>
    </tr>
    <tr>
      <td>
        Aadhaar No : <strong>${guarantor.aadhaar || ''}</strong><br>
        Guarantor Name : <strong>${guarantor.name || ''}</strong><br>
        Father's Name : <strong>${loan.father_name_guarantor || ''}</strong><br>
        Address : <strong>${guarantor.address || ''}</strong><br>
        Phone No : <strong>${guarantor.phone || ''}</strong>
      </td>
      <td class="photo-container">
        ${guarantor.photo ? `<img src="${guarantor.photo}" alt="Guarantor Photo">` : '<p>No photo</p>'}
      </td>
    </tr>
  </table>
  <table>
    <tr>
      <th colspan="2">Secondary Guarantor Details</th>
    </tr>
    <tr>
      <td>
        Secondary surety : <strong>${guarantor.second_guarantor || ''}</strong>
        <p></p>
        <p></p>
        Signature<br>
      </td>
    </tr>
  </table>
  <table>
    <tr>
      <th>Recommended By</th>
      <th>Loan Amount</th>
      <th>Period (Days)</th>
      <th>Monthly Installment</th>
      <th>Due Date</th>
    </tr>
    <tr>
      <td>${loan.recommended_by || ''}</td>
      <td>${loanAmount.toFixed(2)}</td>
      <td>${period || ''}</td>
      <td>${monthlyInstallment.toFixed(2)}</td>
      <td>${dueDate || ''}</td>
    </tr>
  </table>

  <div class="signature-section">
    <div class="signature">Loanee Signature</div>
    <div class="signature">Guarantor Signature</div>
    <div class="signature">Manager/ Money Lender</div>
  </div>
</div>
`;

    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write('<html><head><title>Loan Application Print</title></head><body>');
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  
  return (
    <div style={{ padding: '20px' }}>
     {/* Back Button */}
      <button onClick={handleBackClick} className="back-button">
        Back
      </button>

      {/* No Dues Section */}
      <div style={{ padding: '10px', border: '1px solid black', margin: '10px' }}>
        <h2>No Dues Accounts</h2>
        {noDuesAccounts.length === 0 ? (
          <p>No accounts with no dues.</p>
        ) : (
          <ul>
            {noDuesAccounts.map((account) => (
              <li key={account.account_no}>
                <span
                  className="clickable-account"
                  onClick={() => openDialog(account.account_no)}
                >
                  {account.account_no}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Dues Section */}
      <div style={{ padding: '10px', border: '1px solid black', margin: '10px' }}>
        <h2>Accounts with Dues</h2>
        {duesAccounts.length === 0 ? (
          <p>No accounts with pending dues.</p>
        ) : (
          <ul>
            {duesAccounts.map((account) => (
              <li key={account.account_no}>
                <span
                  className="clickable-account"
                  onClick={() => openDialog(account.account_no)}
                >
                  {account.account_no}
                </span>
                <span className="outstanding">₹{account.outstandingAmount}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {dialogOpen && (
        <div className="account-dialog-overlay" onClick={closeDialog}>
          <div className="account-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <div className="dialog-title">
                Account Details {selectedAccount ? `- ${selectedAccount}` : ''}
              </div>
              <button className="dialog-close" onClick={closeDialog}>×</button>
            </div>

            {loading && (
              <div className="loading-state">
                <div className="spinner" />
                <div>Loading account details...</div>
              </div>
            )}

            {!loading && error && (
              <div className="error-state">{error}</div>
            )}

            {!loading && !error && accountDetails && (
              <div className="dialog-content">
                {/* Summary */}
                <div className="summary-cards">
                  <div className="card">
                    <div className="card-label">Total Loan</div>
                    <div className="card-value">₹{Number(accountDetails?.summary?.total_loan_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="card">
                    <div className="card-label">Total Paid</div>
                    <div className="card-value">₹{Number(accountDetails?.summary?.total_paid_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="card">
                    <div className="card-label">Outstanding</div>
                    <div className="card-value">₹{Number(accountDetails?.summary?.total_remaining_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="card">
                    <div className="card-label">Total OD Days</div>
                    <div className="card-value">{Number(accountDetails?.summary?.total_overdue_days || 0)}</div>
                  </div>
                </div>

                {/* Download Forms Section */}
                <div className="download-forms-section" style={{ marginTop: '20px', marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
                  <div className="section-title" style={{ marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>Download Forms</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <button 
                      onClick={handleDownloadFormNo6} 
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: '#4CAF50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '5px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
                    >
                      Download Form No 6
                    </button>
                    <button 
                      onClick={handleDownloadInterestSlip} 
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: '#2196F3', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '5px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0b7dda'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'}
                    >
                      Download Interest Slip
                    </button>
                    <button 
                      onClick={handleDownloadDebitVoucher} 
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: '#FF9800', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '5px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#e68900'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#FF9800'}
                    >
                      Download Debit Voucher
                    </button>
                    <button 
                      onClick={handleDownloadApplicationForm} 
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: '#9C27B0', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '5px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#7b1fa2'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#9C27B0'}
                    >
                      Download Application Form
                    </button>
                  </div>
                </div>

                {/* Loan Info */}
                <div className="info-grid">
                  <div className="info-section">
                    <div className="section-title">Loan Information</div>
                    <div className="info-row"><span>Account</span><strong>{accountDetails?.loan?.account_no || 'N/A'}</strong></div>
                    <div className="info-row"><span>Date</span><strong>{accountDetails?.loan?.date || 'N/A'}</strong></div>
                    <div className="info-row"><span>Amount</span><strong>₹{Number(accountDetails?.loan?.loan_amount || 0).toFixed(2)}</strong></div>
                    <div className="info-row"><span>Period (days)</span><strong>{accountDetails?.loan?.period || 'N/A'}</strong></div>
                    <div className="info-row"><span>Installment</span><strong>₹{Number(accountDetails?.loan?.monthly_installment || 0).toFixed(2)}</strong></div>
                    <div className="info-row"><span>Recommended By</span><strong>{accountDetails?.loan?.recommended_by || 'N/A'}</strong></div>
                  </div>

                  <div className="info-section">
                    <div className="section-title">Debtor Information</div>
                    <div className="info-row"><span>Name</span><strong>{accountDetails?.debtor?.name || 'N/A'}</strong></div>
                    <div className="info-row"><span>Aadhaar</span><strong>{maskAadhaar(accountDetails?.debtor?.aadhaar || 'N/A')}</strong></div>
                    <div className="info-row"><span>PAN</span><strong>{maskPAN(accountDetails?.debtor?.pan || 'N/A')}</strong></div>
                    <div className="info-row"><span>Phone</span><strong>{accountDetails?.debtor?.phone || 'N/A'}</strong></div>
                    <div className="info-row"><span>Address</span><strong>{accountDetails?.debtor?.address || 'N/A'}</strong></div>
                    <div className="photo-box">
                      {accountDetails?.debtor?.photo ? (
                        <img src={accountDetails?.debtor?.photo} alt="Debtor" />
                      ) : (
                        <div className="no-photo">No Photo</div>
                      )}
                    </div>
                  </div>

                  <div className="info-section">
                    <div className="section-title">Guarantor Information</div>
                    <div className="info-row"><span>Name</span><strong>{accountDetails?.guarantor?.name || 'N/A'}</strong></div>
                    <div className="info-row"><span>Aadhaar</span><strong>{maskAadhaar(accountDetails?.guarantor?.aadhaar || 'N/A')}</strong></div>
                    <div className="info-row"><span>Phone</span><strong>{accountDetails?.guarantor?.phone || 'N/A'}</strong></div>
                    <div className="info-row"><span>Address</span><strong>{accountDetails?.guarantor?.address || 'N/A'}</strong></div>
                    <div className="info-row"><span>Second Guarantor</span><strong>{accountDetails?.guarantor?.second_guarantor || 'N/A'}</strong></div>
                    <div className="photo-box">
                      {accountDetails?.guarantor?.photo ? (
                        <img src={accountDetails?.guarantor?.photo} alt="Guarantor" />
                      ) : (
                        <div className="no-photo">No Photo</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Due Dates Table */}
                <div className="table-section">
                  <div className="section-title">Due Dates</div>
                  <div className="table-wrap">
                    <table className="details-table">
                      <thead>
                        <tr>
                          <th>Due Date</th>
                          <th>Due Amount</th>
                          <th>Paid</th>
                          <th>Remaining</th>
                          <th>Days OD</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(accountDetails?.due_dates) && accountDetails?.due_dates?.length > 0 ? (
                          accountDetails?.due_dates?.map((d, idx) => (
                            <tr key={`${d?.due_date || idx}`} className={d?.is_overdue && d?.remaining_amount > 0 ? 'row-overdue' : ''}>
                              <td>{d?.due_date || 'N/A'}</td>
                              <td>₹{Number(d?.due_amount || 0).toFixed(2)}</td>
                              <td>₹{Number(d?.paid_amount || 0).toFixed(2)}</td>
                              <td>₹{Number(d?.remaining_amount || 0).toFixed(2)}</td>
                              <td>{Number(d?.days_overdue || 0)}</td>
                              <td>
                                {d?.remaining_amount > 0 ? (
                                  <span className="badge badge-overdue">Overdue</span>
                                ) : (
                                  <span className="badge badge-ok">On Track</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={6} style={{ textAlign: 'center' }}>No due dates available</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Transactions */}
                <div className="table-section">
                  <div className="section-title">Transaction History</div>
                  <div className="table-wrap">
                    <table className="details-table">
                      <thead>
                        <tr>
                          <th>Payment Date</th>
                          <th>Amount</th>
                          <th>Mode</th>
                          <th>Transaction ID</th>
                          <th>Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(accountDetails?.transactions) && accountDetails?.transactions?.length > 0 ? (
                          accountDetails?.transactions?.map((t, idx) => (
                            <tr key={`${t?.payment_date || idx}-${t?.transaction_id || idx}`}>
                              <td>{t?.payment_date || 'N/A'}</td>
                              <td>₹{Number(t?.amount || 0).toFixed(2)}</td>
                              <td>{t?.payment_mode || 'N/A'}</td>
                              <td>{t?.transaction_id || 'N/A'}</td>
                              <td>{t?.due_date || 'N/A'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={5} style={{ textAlign: 'center' }}>No transactions found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
